import { 
  DollarSign, 
  Calendar, 
  Receipt, 
  Gift, 
  TrendingUp, 
  AlertCircle,
  Check,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import type { Brokerage } from '@/contracts/brokerage';
import { formatCurrency, formatSplit } from '@/contracts/brokerage';

interface FeeBreakdownCardProps {
  brokerage: Brokerage;
  className?: string;
}

export function FeeBreakdownCard({ brokerage, className }: FeeBreakdownCardProps) {
  const hasAnyMonthlyFee = 
    brokerage.monthly_fee || 
    brokerage.technology_fee || 
    brokerage.eo_fee || 
    brokerage.marketing_fee;

  const totalMonthlyFees = 
    (brokerage.monthly_fee || 0) + 
    (brokerage.technology_fee || 0) + 
    (brokerage.eo_fee || 0) + 
    (brokerage.marketing_fee || 0);

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Receipt className="h-5 w-5 text-primary" />
          Complete Fee Breakdown
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Commission Split */}
        <div>
          <h4 className="font-medium text-sm text-muted-foreground mb-2">Commission Split</h4>
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="text-sm">Your Split</span>
              <span className="text-2xl font-bold text-primary">
                {formatSplit(brokerage.split_starting_percent, brokerage.split_max_percent)}
              </span>
            </div>
            {brokerage.cap_amount && (
              <div className="mt-2 pt-2 border-t border-border/50">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Annual Cap</span>
                  <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                    {formatCurrency(brokerage.cap_amount)}/{brokerage.cap_period || 'year'}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  After hitting the cap, you keep 100% of your commission
                </p>
              </div>
            )}
            {!brokerage.cap_amount && brokerage.split_starting_percent && (
              <p className="text-xs text-muted-foreground mt-2">
                No annual cap — split applies to all transactions
              </p>
            )}
          </div>
        </div>

        {/* Monthly Fees */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              Monthly Fees
            </h4>
            {hasAnyMonthlyFee && (
              <Badge variant="outline" className="text-xs">
                {formatCurrency(totalMonthlyFees)}/mo • {formatCurrency(totalMonthlyFees * 12)}/yr
              </Badge>
            )}
          </div>
          <div className="space-y-2">
            <FeeRow 
              label="Desk / Office Fee" 
              value={brokerage.monthly_fee}
              note={brokerage.desk_fee_optional ? 'Optional' : undefined}
            />
            <FeeRow label="Technology Fee" value={brokerage.technology_fee} />
            <FeeRow label="E&O Insurance" value={brokerage.eo_fee} />
            <FeeRow label="Marketing Fee" value={brokerage.marketing_fee} />
            {!hasAnyMonthlyFee && (
              <p className="text-sm text-muted-foreground italic">No monthly fees</p>
            )}
          </div>
        </div>

        <Separator />

        {/* Transaction Fees */}
        <div>
          <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-1 mb-2">
            <Receipt className="h-4 w-4" />
            Per-Transaction Fees
          </h4>
          {brokerage.transaction_fee ? (
            <div className="bg-muted/50 rounded-lg p-3 flex justify-between items-center">
              <span className="text-sm">Per Transaction</span>
              <span className="font-semibold">
                {brokerage.transaction_fee_type === 'percent' 
                  ? `${brokerage.transaction_fee}%`
                  : formatCurrency(brokerage.transaction_fee)
                }
              </span>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No per-transaction fees</p>
          )}
          {brokerage.royalty_fee_percent && (
            <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3 flex justify-between items-center">
              <span className="text-sm text-amber-900">Franchise Royalty</span>
              <span className="font-semibold text-amber-900">{brokerage.royalty_fee_percent}%</span>
            </div>
          )}
        </div>

        <Separator />

        {/* Sign-on Bonus */}
        <div>
          <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-1 mb-2">
            <Gift className="h-4 w-4" />
            Sign-on Bonus
          </h4>
          {(brokerage.sign_on_bonus_min || brokerage.sign_on_bonus_max) ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-emerald-900">Potential Bonus</span>
                <span className="text-xl font-bold text-emerald-700">
                  {brokerage.sign_on_bonus_min && brokerage.sign_on_bonus_max && brokerage.sign_on_bonus_min !== brokerage.sign_on_bonus_max
                    ? `${formatCurrency(brokerage.sign_on_bonus_min)} - ${formatCurrency(brokerage.sign_on_bonus_max)}`
                    : formatCurrency(brokerage.sign_on_bonus_max || brokerage.sign_on_bonus_min)
                  }
                </span>
              </div>
              {brokerage.sign_on_bonus_notes && (
                <p className="text-xs text-emerald-800 mt-2">{brokerage.sign_on_bonus_notes}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No sign-on bonus advertised</p>
          )}
        </div>

        {/* Revenue Share & Stock */}
        {(brokerage.has_revenue_share || brokerage.has_stock_awards) && (
          <>
            <Separator />
            <div>
              <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-1 mb-2">
                <TrendingUp className="h-4 w-4" />
                Additional Compensation
              </h4>
              <div className="space-y-2">
                <BenefitRow 
                  label="Revenue Share Program" 
                  available={brokerage.has_revenue_share}
                  description={brokerage.revenue_share_description}
                />
                <BenefitRow 
                  label="Stock Awards / Equity" 
                  available={brokerage.has_stock_awards}
                  description={brokerage.stock_awards_description}
                />
              </div>
            </div>
          </>
        )}

        {/* Fee Notes */}
        {brokerage.fee_notes && (
          <>
            <Separator />
            <div className="flex items-start gap-2 text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p>{brokerage.fee_notes}</p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function FeeRow({ 
  label, 
  value, 
  note 
}: { 
  label: string; 
  value: number | null | undefined; 
  note?: string;
}) {
  if (!value) return null;
  
  return (
    <div className="flex justify-between items-center py-1.5 px-3 bg-muted/30 rounded">
      <span className="text-sm">
        {label}
        {note && <span className="text-xs text-muted-foreground ml-1">({note})</span>}
      </span>
      <span className="font-medium">{formatCurrency(value)}/mo</span>
    </div>
  );
}

function BenefitRow({ 
  label, 
  available, 
  description 
}: { 
  label: string; 
  available?: boolean; 
  description?: string | null;
}) {
  return (
    <div className={`p-3 rounded-lg ${available ? 'bg-emerald-50 border border-emerald-200' : 'bg-muted/30'}`}>
      <div className="flex items-center gap-2">
        {available ? (
          <Check className="h-4 w-4 text-emerald-600" />
        ) : (
          <X className="h-4 w-4 text-muted-foreground" />
        )}
        <span className={`text-sm font-medium ${available ? 'text-emerald-900' : 'text-muted-foreground'}`}>
          {label}
        </span>
      </div>
      {available && description && (
        <p className="text-xs text-emerald-800 mt-1 ml-6">{description}</p>
      )}
    </div>
  );
}
